import {
  ADVANCED_FIELD_KEYS,
  BandBounds,
  FormulaFieldKey,
  getBandBounds,
  getSharedAdvancedFormula,
  MULTIPLIER_FORMULA_FIELD_KEY,
} from "portal/utils/shootTime";
import { Almanac, Role } from "protos/robot_syncer/plant_profile";
import { Formula } from "protos/almanac/almanac";
import {
  hasVisibleShootTime,
  ResolvedOriginCategory,
  resolveOriginCategory,
} from "portal/utils/categoryCollection/category";
import { isEqual } from "portal/utils/equality";

/**
 * Band ranges for one version, empty when the almanac is missing. History has
 * no chart domain to cap the last band, so it stays open-ended (infinite
 * upper) rather than borrowing the editor's max radius.
 */
const getVersionBandBounds = (almanac: Almanac | undefined): BandBounds[] =>
  almanac && almanac.sizeBoundariesMm.length > 0
    ? getBandBounds(almanac.sizeBoundariesMm, Number.POSITIVE_INFINITY)
    : [];

export type ValueChangeKind = "unchanged" | "added" | "removed" | "replaced";

export type ValueHistory<T> = {
  before?: T;
  after?: T;
  change: ValueChangeKind;
};

/** Without a version to compare against, every value reads as unchanged. */
export const valueHistory = <T>(
  before: T | undefined,
  after: T | undefined,
  comparing: boolean
): ValueHistory<T> => {
  if (!comparing || isEqual(before, after)) {
    return { after, change: "unchanged" };
  }
  if (before === undefined) {
    return { after, change: "added" };
  }
  if (after === undefined) {
    return { before, change: "removed" };
  }
  return { before, after, change: "replaced" };
};

export type AdvancedFieldHistory = ValueHistory<number> & {
  key: FormulaFieldKey;
};

const getAdvancedHistory = (
  before: Formula | undefined,
  after: Formula | undefined,
  comparing: boolean
): AdvancedFieldHistory[] =>
  ADVANCED_FIELD_KEYS.map((key) => ({
    key,
    ...valueHistory(before?.[key], after?.[key], comparing),
  }));

const getMultiplierHistory = (
  before: Formula | undefined,
  after: Formula | undefined,
  comparing: boolean
): ValueHistory<number> =>
  valueHistory(
    before?.[MULTIPLIER_FORMULA_FIELD_KEY],
    after?.[MULTIPLIER_FORMULA_FIELD_KEY],
    comparing
  );

const sharesAdvancedOrAbsent = (
  formulas: readonly Formula[] | undefined
): boolean => !formulas?.length || Boolean(getSharedAdvancedFormula(formulas));

export type BandShootTime = {
  index: number;
  bounds: ValueHistory<BandBounds>;
  multiplier: ValueHistory<number>;
  advanced: AdvancedFieldHistory[];
};

export type VersionShootTime = {
  bands: BandShootTime[];
  mostSimilarTo: ValueHistory<ResolvedOriginCategory>;
  sharedAdvanced?: AdvancedFieldHistory[];
  changes: { added: number; removed: number };
};

const CHANGE_COUNTS: Record<
  ValueChangeKind,
  { added: number; removed: number }
> = {
  unchanged: { added: 0, removed: 0 },
  added: { added: 1, removed: 0 },
  removed: { added: 0, removed: 1 },
  replaced: { added: 1, removed: 1 },
};

const countChanges = (
  histories: ValueHistory<unknown>[]
): { added: number; removed: number } =>
  histories.reduce(
    (total, { change }) => ({
      added: total.added + CHANGE_COUNTS[change].added,
      removed: total.removed + CHANGE_COUNTS[change].removed,
    }),
    { added: 0, removed: 0 }
  );

/**
 * The version being compared against. Passing this with no almanac means the
 * category is new, so its values read as added rather than unchanged.
 */
export type ShootTimeComparison = { almanac?: Almanac };

export const getVersionShootTime = (
  current: Almanac | undefined,
  comparison?: ShootTimeComparison,
  role?: Role
): VersionShootTime => {
  if (role !== undefined && !hasVisibleShootTime(role)) {
    return {
      bands: [],
      mostSimilarTo: valueHistory<ResolvedOriginCategory>(
        undefined,
        undefined,
        false
      ),
      changes: { added: 0, removed: 0 },
    };
  }
  const comparing = comparison !== undefined;
  const versus = comparison?.almanac;
  const mostSimilarTo = valueHistory(
    resolveOriginCategory(versus?.originCategoryId),
    resolveOriginCategory(current?.originCategoryId),
    comparing
  );
  const currentBounds = getVersionBandBounds(current);
  const versusBounds = getVersionBandBounds(versus);
  const bandCount = Math.max(currentBounds.length, versusBounds.length);
  const bands: BandShootTime[] = [];

  for (let index = 0; index < bandCount; index++) {
    const beforeFormula = versus?.formulas[index];
    const afterFormula = current?.formulas[index];
    bands.push({
      index,
      bounds: valueHistory(
        versusBounds[index],
        currentBounds[index],
        comparing
      ),
      multiplier: getMultiplierHistory(beforeFormula, afterFormula, comparing),
      advanced: getAdvancedHistory(beforeFormula, afterFormula, comparing),
    });
  }

  const sharedAdvanced =
    (current?.formulas[0] || versus?.formulas[0]) &&
    sharesAdvancedOrAbsent(current?.formulas) &&
    sharesAdvancedOrAbsent(versus?.formulas)
      ? getAdvancedHistory(versus?.formulas[0], current?.formulas[0], comparing)
      : undefined;

  // Multipliers are tuned per band even when the advanced base is shared.
  const changes = countChanges([
    mostSimilarTo,
    ...bands.map((band) => band.bounds),
    ...bands.map((band) => band.multiplier),
    ...(sharedAdvanced ?? bands.flatMap((band) => band.advanced)),
  ]);

  return { bands, mostSimilarTo, sharedAdvanced, changes };
};
